import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import type { CSSProperties, KeyboardEvent } from 'react';

/**
 * ProgressSteps — the 3-step wizard stepper (DESIGN.md §4 · wizard
 * steps). States:
 *
 *   inactive  → muted number + label + dim dashed divider
 *   active    → green number circle, bright label, accent divider to
 *               the previously-completed step
 *   done      → ✓ replaces the number, green tint, divider after it
 *               painted in accent
 *
 * Forward navigation is locked: the user cannot click ahead past the
 * highest step they've reached (mirrors the legacy `goToStep` guard).
 */
export type WizardStepId = 1 | 2 | 3;

export interface StepDefinition {
  id: WizardStepId;
  label: string;
  description?: string;
}

export const DEFAULT_STEPS: readonly StepDefinition[] = [
  { id: 1, label: 'Room', description: 'Dimensions' },
  { id: 2, label: 'Objects', description: 'Treatment' },
  { id: 3, label: 'Analyze', description: 'RT60' },
];

export interface ProgressStepsProps {
  currentStep: WizardStepId;
  /** Highest step the user has reached — used to lock forward jumps. */
  highestStep?: WizardStepId;
  onStepClick?: (step: WizardStepId) => void;
  steps?: readonly StepDefinition[];
  className?: string;
  style?: CSSProperties;
}

type StepVisualState = 'inactive' | 'active' | 'done';

function getState(step: WizardStepId, current: WizardStepId): StepVisualState {
  if (step < current) return 'done';
  if (step === current) return 'active';
  return 'inactive';
}

export default function ProgressSteps({
  currentStep,
  highestStep = currentStep,
  onStepClick,
  steps = DEFAULT_STEPS,
  className,
  style,
}: ProgressStepsProps) {
  return (
    <div
      className={className}
      role="tablist"
      aria-label="Wizard progress"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-sm)',
        width: '100%',
        ...style,
      }}
    >
      {steps.map((step, idx) => {
        const state = getState(step.id, currentStep);
        const reachable = step.id <= highestStep;
        const clickable = Boolean(onStepClick) && reachable;
        const nextReached = idx < steps.length - 1 && steps[idx + 1]!.id <= highestStep;

        return (
          <div key={step.id} style={{ display: 'contents' }}>
            <StepItem
              step={step}
              state={state}
              clickable={clickable}
              onClick={
                clickable && onStepClick
                  ? () => onStepClick(step.id)
                  : undefined
              }
            />
            {idx < steps.length - 1 && (
              <div
                aria-hidden
                className={`wizard-divider${nextReached ? ' wizard-divider--done' : ''}`}
                style={{ marginTop: 14 /* center on the 28px circle */ }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Step item ────────────────────────────────────────────────────── */

interface StepItemProps {
  step: StepDefinition;
  state: StepVisualState;
  clickable: boolean;
  onClick?: () => void;
}

function StepItem({ step, state, clickable, onClick }: StepItemProps) {
  const circleStyles = CIRCLE_STYLES[state];
  const labelColor = LABEL_COLOR[state];

  function handleKey(e: KeyboardEvent<HTMLDivElement>) {
    if (!clickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  }

  return (
    <motion.div
      role="tab"
      aria-selected={state === 'active'}
      aria-disabled={!clickable && state === 'inactive' ? true : undefined}
      tabIndex={clickable ? 0 : -1}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? handleKey : undefined}
      whileTap={clickable ? { scale: 0.97 } : undefined}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-xs)',
        cursor: clickable ? 'pointer' : 'default',
        padding: 'var(--space-xs) var(--space-sm)',
        borderRadius: 'var(--radius-md)',
        minWidth: 72,
        outline: 'none',
      }}
    >
      {/* Circle: pure CSS transitions (200ms ease-out) on the animated
          properties. Keeps framer-motion typings happy and stays perf-y. */}
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
          fontSize: 13,
          lineHeight: 1,
          transition:
            'background-color 0.2s ease-out, color 0.2s ease-out, border-color 0.2s ease-out, box-shadow 0.2s ease-out',
          ...circleStyles,
        }}
      >
        {state === 'done' ? <Check size={14} strokeWidth={2.5} /> : step.id}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontWeight: 600,
          fontSize: 11,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: labelColor,
          lineHeight: 1,
        }}
      >
        {step.label}
      </div>
      {step.description && (
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 10,
            fontWeight: 400,
            color: 'var(--text-hint)',
            textAlign: 'center',
            lineHeight: 1.3,
            letterSpacing: 0,
            textTransform: 'none',
          }}
        >
          {step.description}
        </div>
      )}
    </motion.div>
  );
}

/* ─── Styling tables — keeps the state → token mapping explicit ─── */

const CIRCLE_STYLES: Record<StepVisualState, CSSProperties> = {
  inactive: {
    background: 'var(--bg-panel-2)',
    color: 'var(--text-muted)',
    border: '1px solid var(--border-mid)',
    boxShadow: 'none',
  },
  active: {
    background: 'var(--accent-green)',
    color: '#fff',
    border: '1px solid var(--accent-green)',
    boxShadow: '0 0 20px rgba(112, 148, 36, 0.25)',
  },
  done: {
    background: 'rgba(112, 148, 36, 0.15)',
    color: 'var(--accent-green)',
    border: '1px solid rgba(112, 148, 36, 0.45)',
    boxShadow: 'none',
  },
};

const LABEL_COLOR: Record<StepVisualState, string> = {
  inactive: 'var(--text-muted)',
  active: 'var(--text-primary)',
  done: 'var(--accent-green)',
};
